import { TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryOptionsGetTaskFilterOptions } from '../api/task/taskApi';
import { TaskCardProps } from '../components/TaskCard';
import {
  UNKNOWN_CLAIMANT_LABEL,
  optionClientNames,
  optionOtherClaimantNames,
} from '../pages/v3/Tasks/taskFilterOptions';
import { WorkspaceTask } from '../types/taskTypes';
import {
  createClientsMap,
  extractUniqueClientIds,
  getOtherClaimantKey,
  getOtherClaimantName,
  getTaskClientId,
} from '../utils/helpers/clients';
import { workspaceTypeIsAgent } from '../utils/helpers/common';
import { useActiveWorkspace } from './useActiveWorkspace';
import { UNRESOLVED_CLIENT_NAME, useClientNamesByIds } from './useClientNames';

type TaskPartyNames = Pick<
  TaskCardProps,
  | 'clientName'
  | 'hasClientField'
  | 'isClientNamePending'
  | 'isOtherClaimantNamePending'
  | 'otherClaimantName'
>;

/**
 * Card names for a task's own client (agent workspaces only: MSR2 stamps every
 * task with a portfolio client) and a competing claim's other claimant, which
 * only the filter options can name since it belongs to another workspace.
 */
export const useTaskPartyNames = (
  tasks: WorkspaceTask[],
  workspaceId?: string,
  {
    includeResolved,
    type,
  }: {
    /**
     * Set when `tasks` may hold resolved competing claims, whose other
     * claimants the open-only options can't name.
     */
    includeResolved?: boolean;
    type?: TaskTypeEnum;
  } = {},
) => {
  const [activeWorkspace, activeWorkspaceQuery] = useActiveWorkspace();

  // Placeholder data is the previous workspace's, so it can't say whether
  // this one is an agent.
  const isWorkspaceTypePending =
    tasks.some((task) => Boolean(getTaskClientId(task))) &&
    (activeWorkspaceQuery.isLoading || activeWorkspaceQuery.isPlaceholderData);
  const isAgent =
    !isWorkspaceTypePending && workspaceTypeIsAgent(activeWorkspace);
  const clientIds = useMemo(
    () => (isAgent ? extractUniqueClientIds(tasks) : []),
    [isAgent, tasks],
  );
  const hasCompetingClaim = tasks.some(
    (task) => task.type === TaskTypeEnum.CompetingClaim,
  );

  const {
    clientNameById,
    clientsQueries,
    isError: isClientsError,
    isLoading: isClientsLoading,
  } = useClientNamesByIds(clientIds, { enabled: isAgent, workspaceId });

  /**
   * The previous page's names, held as placeholder data while the new page's
   * batch loads. A name doesn't depend on the page, so a client already shown
   * keeps its name instead of flashing back to pending on every page change.
   */
  const placeholderClientNameById = createClientsMap(
    clientsQueries
      .filter((query) => query.isPlaceholderData)
      .flatMap((query) => query.data?.items ?? []),
  );

  // `type` left undefined skips the request (`skipToken`).
  const filterOptionsQuery = useQuery(
    queryOptionsGetTaskFilterOptions({
      includeResolved,
      type:
        type ?? (hasCompetingClaim ? TaskTypeEnum.CompetingClaim : undefined),
      workspaceId,
    }),
  );

  const { optionClientNameById, otherClaimantNameByKey } = useMemo(
    () => ({
      optionClientNameById: optionClientNames(filterOptionsQuery.data?.clients),
      otherClaimantNameByKey: optionOtherClaimantNames(
        filterOptionsQuery.data?.otherClaimants,
        UNKNOWN_CLAIMANT_LABEL,
      ),
    }),
    [filterOptionsQuery.data],
  );

  const getPartyNames = (task: WorkspaceTask): TaskPartyNames => {
    const clientId = isAgent ? getTaskClientId(task) : undefined;
    // Fresh lookup first: the filter options can be up to a minute stale.
    const clientName = clientId
      ? clientNameById.get(clientId)?.clientName ||
        optionClientNameById.get(clientId) ||
        placeholderClientNameById.get(clientId)?.clientName ||
        // A failed request, not an unknown id: that one stays blank.
        (isClientsError && !isClientsLoading
          ? UNRESOLVED_CLIENT_NAME
          : undefined)
      : undefined;
    const ownClient = {
      clientName,
      hasClientField: isAgent,
      isClientNamePending: Boolean(clientId) && !clientName && isClientsLoading,
    };

    if (task.type !== TaskTypeEnum.CompetingClaim) return ownClient;

    return {
      ...ownClient,
      isOtherClaimantNamePending:
        Boolean(getOtherClaimantKey(task)) && filterOptionsQuery.isLoading,
      otherClaimantName: getOtherClaimantName(task, otherClaimantNameByKey),
    };
  };

  return { getPartyNames, isWorkspaceTypePending };
};
